/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import {EViewControls, EViewControlsFamily, MouseActionEnum} from "./scene_view_controls_enums";
import {RenderDocument, ViewMode as RenderViewMode, Camera, Debug, Canvas, ViewControls as RenderViewControls} from "js_renderer";
import Scene from "../../scene";
import AbstractModule from "../abstract_module";
import IViewControlsModule from "./scene_view_controls_interface";
import Settings from "../settings";
import {vec2} from "gl-matrix";

interface IMouseButtonsFunctionality {
    middle?: MouseActionEnum;
    left?: MouseActionEnum;
    right?: MouseActionEnum;
}

interface IViewControlsSettings {
    mouse?: IMouseButtonsFunctionality;
    interocularDistance?: number;
    viewAngle?: number;
}

/***
 * Wrapper for utils.BoundingBox for usage in the application
 * @constructor
 */
export default
class ViewControlsModule extends AbstractModule implements IViewControlsModule {

    protected static _typeName: string = "viewControls";

    protected _defaultSettings: IViewControlsSettings;
    protected _settings: IViewControlsSettings;

    private _controls: RenderViewControls;

    private _deviceOrientationEventEnabled: boolean;

    constructor(aRenderDocument: RenderDocument) {
        super(aRenderDocument);
        this._type = ViewControlsModule._typeName;

        this._controls = null;

        this._defaultSettings = {
            mouse: { // to view controls
                left: MouseActionEnum.ROTATE,
                middle: MouseActionEnum.PAN,
                right: MouseActionEnum.ROTATE
            },
            interocularDistance: 75, // mm
            viewAngle: 60 // degrees
        };

        this._deviceOrientationEventEnabled = true; // to view controls

        this._settings = Settings.clone(this._defaultSettings);

        this.setActiveViewControls(EViewControls.EXAMINER);
    }

    public static getClassType(): string {
        return ViewControlsModule._typeName;
    }

    public getControlsType(): EViewControls {
        return this._controls.getControlsType();
    }

    public getControlsTypeFamily(): EViewControlsFamily {
        return this._controls.getControlsTypeFamily();
    }

    public attachViewControls(aViewControls: RenderViewControls) {
        this._controls = aViewControls;
    }

    public setViewControls(aViewControlsType: EViewControls) {
        this.setActiveViewControls(aViewControlsType);
    }

    public setActiveViewControls(aViewControlsType: EViewControls) {

        if (!this.sceneAPI.viewModeModule) {
            return;
        }
        let view = this.sceneAPI.viewModeModule.getActiveView();

        view.setViewControls(aViewControlsType, view.getViewControls());
        this._controls = view.getViewControls();
    }

    public getCamera(): Camera {
        return this._controls.getCamera();
    }

    public getViewControlsWalkModeRealSpeed(): number {
        if (this._controls.getControlsType() === EViewControls.WALK ||
            this._controls.getControlsType() === EViewControls.WALK_STEREO ||
            this._controls.getControlsType() === EViewControls.WALK_WEBVR) {
            return this._controls.getRealSpeed();
        } else {
            Debug.logInfo('Speed can be get only in walking mode controls');
            return 0;
        }
    }

    public setViewControlsWalkModeSpeed(aSpeed: number) {
        if (this._controls.getControlsType() === EViewControls.WALK ||
            this._controls.getControlsType() === EViewControls.WALK_STEREO ||
            this._controls.getControlsType() === EViewControls.WALK_WEBVR) {
            this._controls.setSpeed(aSpeed);
        } else {
            Debug.logInfo('Speed can be set only in walking mode controls');
        }
    }

    public getViewAngleLimits(): { min: number, max: number } {
        return this._controls.getViewAngleLimits();
    }

    public getViewAngle(): number {
        // return this._controls.getViewAngle();
        return this._settings.viewAngle;
    }

    public setFreeLook(aValue: boolean) {
        this._controls.setFreeLook(aValue);
    }

    private setViewAngle(aViewAngle: number) {
        this._controls.setViewAngle(aViewAngle);
    }

    public setLeftMouseButtonFunctionality(aValue: MouseActionEnum) {
        this._settings.mouse.left = aValue;
    }

    public setMiddleMouseButtonFunctionality(aValue: MouseActionEnum) {
        this._settings.mouse.middle = aValue;
    }

    public setRightMouseButtonFunctionality(aValue: MouseActionEnum) {
        this._settings.mouse.right = aValue;
    }

    public setViewControlsWalkModeViewAngle(aViewAngle: number) {
        if (this._controls.getControlsType() === EViewControls.WALK ||
            this._controls.getControlsType() === EViewControls.WALK_STEREO ||
            this._controls.getControlsType() === EViewControls.WALK_WEBVR) {
            this._controls.setViewAngle(aViewAngle);
        } else {
            Debug.logInfo('View angle can be set only in walking mode controls');
        }
    }

    public setViewControlsWalkModeMouseSensitivity(aValue: number) {
        if (this._controls.getControlsType() === EViewControls.WALK ||
            this._controls.getControlsType() === EViewControls.WALK_STEREO ||
            this._controls.getControlsType() === EViewControls.WALK_WEBVR) {
            this._controls.setMouseSensitivity(aValue);
        } else {
            Debug.logInfo('Mouse sensitivity can be set only in walking mode controls');
        }
    }

    public setViewControlsWalkModeAvatarHeight(aValue: number) {
        if (this._controls.getControlsType() === EViewControls.WALK ||
            this._controls.getControlsType() === EViewControls.WALK_STEREO ||
            this._controls.getControlsType() === EViewControls.WALK_WEBVR) {
            this._controls.setAvatarHeight(aValue);
        } else {
            Debug.logInfo('Avatar height can be set only in walking mode controls');
        }
    }

    public setViewControlsFlyModeSpeed(aSpeed: number) {
        if (this._controls.getControlsType() === EViewControls.FLY ||
            this._controls.getControlsType() === EViewControls.FLY_STEREO ||
            this._controls.getControlsType() === EViewControls.FLY_WEBVR) {
            this._controls.setSpeed(aSpeed);
        } else {
            Debug.logInfo('Speed can be set only in flying mode controls');
        }
    }

    public setViewControlsFlyModeViewAngle(aViewAngle: number) {
        if (this._controls.getControlsType() === EViewControls.FLY ||
            this._controls.getControlsType() === EViewControls.FLY_STEREO ||
            this._controls.getControlsType() === EViewControls.FLY_WEBVR) {
            this._controls.setViewAngle(aViewAngle);
        } else {
            Debug.logInfo('View angle can be set only in flying mode controls');
        }
    }

    public setViewControlsFlyModeMouseSensitivity(aValue: number) {
        if (this._controls.getControlsType() === EViewControls.FLY ||
            this._controls.getControlsType() === EViewControls.FLY_STEREO ||
            this._controls.getControlsType() === EViewControls.FLY_WEBVR) {
            this._controls.setMouseSensitivity(aValue);
        } else {
            Debug.logInfo('Mouse sensitivity can be set only in flying mode controls');
        }
    }

    public setViewControlsFlyModeAcceleration(aAcceleration: number) {
        if (this._controls.getControlsType() === EViewControls.FLY ||
            this._controls.getControlsType() === EViewControls.FLY_STEREO ||
            this._controls.getControlsType() === EViewControls.FLY_WEBVR
        ) {
            this._controls.setAcceleration(aAcceleration);
        } else {
            Debug.logInfo('Fly mode acceleration can be set only in flying stereo mode controls');
        }
    }

    public setInterOcularDistance(aValue: number) {
        this._settings.interocularDistance = aValue;
    }

    public setEnableDeviceInput(aValue: boolean, aKeepViewDirection: boolean) {
        this._controls.setEnableDeviceInput(aValue, aKeepViewDirection);
    }

    public setFirstPersonMode(aValue: boolean) {
        this._controls.setFirstPersonMode(aValue);
    }

    public setDeviceOrientationEventEnabled(aValue: boolean) {
        this._deviceOrientationEventEnabled = aValue;
    }

    public getLeftMouseButtonFunctionality(): MouseActionEnum {
        return this._settings.mouse.left;
    }

    public getMiddleMouseButtonFunctionality(): MouseActionEnum {
        return this._settings.mouse.middle;
    }

    public getRightMouseButtonFunctionality(): MouseActionEnum {
        return this._settings.mouse.right;
    }

    public getViewControlsFlyModeRealSpeed(): number {
        if (this._controls.getControlsType() === EViewControls.FLY ||
            this._controls.getControlsType() === EViewControls.FLY_STEREO ||
            this._controls.getControlsType() === EViewControls.FLY_WEBVR) {
            return this._controls.getRealSpeed();
        } else {
            Debug.logInfo('Speed can be get only in flying mode controls');
            return 0;
        }
    }

    public getDeviceOrientationEventEnabled(): boolean {
        return this._deviceOrientationEventEnabled;
    }

    public getInterOcularDistance(): number {
        return this._settings.interocularDistance;
    }

    public getDeviceInputEnabled(): boolean {
        return this._controls.getDeviceInputEnabled();
    }

    public isMoving(): boolean {
        return this._controls.isMoving();
    }

    public pickAndSetCameraTarget() {
        this._controls.pickAndSetCameraTarget();
    }

    public viewChanged() {
        //
    }

    public viewResized(aContentSize: vec2) {
        //
    }

    public setFromModelDescription(aJsonObject: any) {
        let settings: IViewControlsSettings = {};
        if (aJsonObject.hasOwnProperty(ViewControlsModule._typeName)) {
            settings = aJsonObject[ViewControlsModule._typeName];
        }
        this.set(settings);
    }

    protected applySettings(aChangedSettings: any, aPreviousSettings: any) {
        if (aChangedSettings) {
            if (aChangedSettings.hasOwnProperty('interocularDistance')) {
                this.setInterOcularDistance(aChangedSettings.interocularDistance);
            }

            if (aChangedSettings.hasOwnProperty('viewAngle')) {
                this.setViewAngle(aChangedSettings.viewAngle);
            }

            // if (aChangedSettings.hasOwnProperty('sunlight')) {
            //     let sunlight = aChangedSettings.sunlight;
            //     if (sunlight.hasOwnProperty('sunlightIntensity')) {
            //         this.setSunLightIntensity(sunlight.sunlightIntensity);
            //         this.enableSunLight(sunlight.sunlightIntensity > 0);
            //     }
            //     if (sunlight.hasOwnProperty('sunlightEnabled')) {
            //         this.enableSunLight(sunlight.sunlightEnabled);
            //     }
            // }
        }
    }
}

Scene.registerSceneAPIModule(ViewControlsModule);
